/**
 * TotpEnrollPanel (AUTH-MFA-01) — the three-step "add an authenticator" flow:
 * scan the code → type the 6 digits → done. Used by Settings → Sign-In
 * Security and by the required-enrollment screen at sign-in.
 */
import { useState, type FormEvent } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { startEnroll, verifyCode, useInvalidateMfa, type Enrollment } from "./mfa";

export function TotpEnrollPanel({ onDone, compact = false }: { onDone?: () => void; compact?: boolean }) {
  const invalidate = useInvalidateMfa();
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [starting, setStarting] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function begin() {
    setError(null);
    setStarting(true);
    try {
      setEnrollment(await startEnroll());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!enrollment) return;
    setError(null);
    setVerifying(true);
    try {
      await verifyCode(enrollment.factorId, code);
      invalidate();
      onDone?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setVerifying(false);
    }
  }

  if (!enrollment) {
    return (
      <div className={compact ? "" : "mt-3"}>
        <button type="button" onClick={begin} disabled={starting}
          className="inline-flex items-center gap-2 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:opacity-40">
          {starting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
          Set up an authenticator app
        </button>
        {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-3 grid gap-4 sm:grid-cols-[176px_1fr]">
      <div className="rounded-lg border border-hairline bg-white p-2">
        <img src={enrollment.qrCode} alt="Scan this with your authenticator app" className="h-40 w-40" />
      </div>
      <div className="min-w-0 text-sm text-slate-700">
        <ol className="list-decimal space-y-1.5 pl-5 text-[13.5px]">
          <li>Open an authenticator app on your phone (Google Authenticator, Microsoft Authenticator, or 1Password) and scan the square.</li>
          <li>Can't scan? Enter this key by hand: <code className="select-all rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[12.5px] text-slate-800">{enrollment.secret}</code></li>
          <li>Type the 6-digit code the app shows to finish.</li>
        </ol>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]*"
            maxLength={7}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123 456"
            className="w-32 rounded-md border border-hairline bg-white px-2.5 py-1.5 font-mono text-[15px] tracking-[0.2em] text-slate-900 focus:border-brand-400 focus:outline-none"
          />
          <button type="submit" disabled={verifying || code.replace(/\s/g, "").length !== 6}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:opacity-40">
            {verifying && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Turn on two-factor
          </button>
          <button type="button" onClick={() => { setEnrollment(null); setCode(""); setError(null); }}
            className="text-xs font-medium text-slate-500 hover:text-slate-800">Cancel</button>
        </div>
        {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
      </div>
    </form>
  );
}
